import classNames from 'classnames';
import Collapsible from 'react-collapsible';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { Alert, App, Col, Divider, Row } from 'antd';
import { useRegisterDormitoryRoom, useValidEntranceDormitory } from '@/hooks/app';
import { Flex, IcArrow, LoadingSpin, PageContainer, PageError } from '@/components';
import { useDormitoryGroupManagement, useDormitoryRequests, useModal } from '@/hooks';
import { Routes } from '@/routes';
import RoomCardGroup from './RoomCardGroup';
import RoomMembersModal from './RoomMembersModal';
import ErrorNoValidPreRegister from '../ErrorNoValidPreRegister';
import DOMPurify from 'dompurify';

export default function DormitoryGroupRoomSelector() {

  const navigate = useNavigate();
  const membersModal = useModal();
  const { message } = App.useApp();
  const [t] = useTranslation(['common', 'dormitory']);
  const { groupRoomConfirm } = useRegisterDormitoryRoom();
  const { validEntrance, fetchValidEntrance, error: entranceError, loading: entranceLoading } = useValidEntranceDormitory();
  const { findRequestBySemesters, error, fetchDormitoryRequests, loading } = useDormitoryRequests(true);
  const { group, fetchGroup, fetching, groupBlocks, groupBlocksError, fetchGroupBlocksRooms, fetchingGroupBlocks } = useDormitoryGroupManagement();

  const pageTitle = t('dormitory:labels.pre-register');
  const backText = t('common:labels.back');

  useEffect(() => {
    fetchGroup();
    fetchDormitoryRequests();
    fetchGroupBlocksRooms();
    fetchValidEntrance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || fetching || fetchingGroupBlocks || entranceLoading) {
    return (<Flex align="center" className="h-full pb-20"><LoadingSpin /></Flex>);
  }

  if (error) return <PageError title={pageTitle} content={error} action={fetchDormitoryRequests} />;
  if (entranceError) return <PageError title={pageTitle} content={entranceError} action={() => navigate(Routes.DormitoryRegister)} actionText={backText} />;
  if (groupBlocksError) return <PageError title={pageTitle} content={groupBlocksError} action={() => navigate(Routes.DormitoryRegister)} actionText={backText} />;

  if (!['APPROVED', 'FINAL'].includes(group?.status)) {
    message.warning({ content: t('dormitory:messages.members-not-confirmed-yet') });
    return <Navigate to={Routes.DormitoryRegister} />;
  }

  const validRequest = findRequestBySemesters(validEntrance?.semesters, true);
  if (!validRequest) return <ErrorNoValidPreRegister />;

  return (
    <PageContainer title={t('dormitory:labels.select-room')}>
      <Row justify="center" className="w-full">
        <Col xxl={24} xl={10} lg={12} md={14} xs={24} span={24}>
          <Flex className="text-lg font-bold justify-center">
            <span>{t('dormitory:labels.dormitory')}</span>
            <span>{validRequest?.destinationDormitory?.name}</span>
          </Flex>
          <Divider />
        </Col>
      </Row>
      <Row className="w-full" justify="center">
        <Col xxl={8} xl={10} lg={12} md={14} xs={24} span={24}>
          <Alert className="w-full" type="warning" message={(
            <div className="leading-loose">
              {t('dormitory:messages.all-members-confirmed')} <br />
              <span dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(t('dormitory:messages.leader-can-continue-registration'), { ALLOWED_TAGS: ['b'] })
              }}></span>
            </div>
          )} />
        </Col>
      </Row>
      <Row className="w-full pt-4" justify="center">
        <Col xl={17} span={24}>
          <Flex className={classNames('p-4 rounded-lg bg-gray-100')}>
            <Flex className="w-full" direction="column">
              <Flex className="w-full" direction="column">
                <RoomMembersModal  {...membersModal} />
                {groupBlocks.map((block, i) => (
                  <Collapsible
                    key={i}
                    open
                    transitionTime={200}
                    transitionCloseTime={200}
                    trigger={(
                      <Flex className="select-none cursor-pointer" align="center">
                        <span className="inline-flex text-lg font-medium">{block.name}</span>
                        <Flex className="pointer-events-auto ms-auto pe-2" inlineFlex>
                          <IcArrow className="reactive-collapsible-arrow" type="down" />
                        </Flex>
                      </Flex>
                    )}>
                    <div>
                      <Divider />
                      <Row gutter={[12, 12]}>
                        {(block?.floors ?? [])?.map((floor, i) => (
                          (floor?.rooms ?? [])?.map((room, j) =>
                            <RoomCardGroup
                              room={room}
                              floor={floor}
                              key={`${i}-${j}`}
                              onSelectRoom={() => groupRoomConfirm(room, () => navigate(Routes.DormitoryRegister))}
                            />
                          )
                        ))}
                      </Row>
                    </div>
                  </Collapsible>
                ))}
              </Flex>

            </Flex>
          </Flex>
        </Col>
      </Row>
    </PageContainer>
  );
}
